import { useMemo, useState } from "react";

import Card from "@mui/material/Card";
import CircularProgress from "@mui/material/CircularProgress";
import Grid from "@mui/material/Grid";

import SoftBox from "components/SoftBox";
import SoftButton from "components/SoftButton";
import SoftInput from "components/SoftInput";
import SoftTypography from "components/SoftTypography";
import { useLocalizedNavigate } from "lib/i18n/navigation";

import { canSendSiteMessage, pickSiteExampleKeys } from "./provider-site";
import { SiteHeader } from "./site-header";
import { SiteLayout } from "./site-layout";
import { useSendSiteMessage } from "./site.hooks";
import { notifyError } from "./site.notify";
import { SITE_CHAT_ROUTE, SITE_ROUTE } from "./site.routes";
import { useSiteTranslations } from "./use-site-translations";

function DescribeSite({ profileUuid }: { profileUuid: string }) {
  const { t } = useSiteTranslations();
  const navigate = useLocalizedNavigate();
  const [brief, setBrief] = useState("");
  const exampleKeys = useMemo(() => pickSiteExampleKeys(), []);
  const [sendMessage, { loading: isSending }] = useSendSiteMessage();
  const canCreate = canSendSiteMessage(null, brief) && !isSending;
  const examples = t.empty.examples as Record<string, string>;

  async function onCreate() {
    if (!canCreate) return;
    try {
      await sendMessage({ variables: { profileUuid, text: brief.trim() } });
      navigate(SITE_CHAT_ROUTE, { replace: true });
    } catch {
      notifyError(t.errors.sendFailedTitle, t.errors.tryAgain);
    }
  }

  return (
    <Grid container spacing={3} data-testid="provider-site-create-screen">
      <Grid item xs={12}>
        <SiteHeader title={t.empty.heading} description={t.empty.body} backTo={SITE_ROUTE} />
      </Grid>
      <Grid item xs={12} lg={5}>
        <Card sx={{ height: "100%" }}>
          <SoftBox p={3} display="flex" flexDirection="column" gap={1.5}>
            <SoftTypography
              variant="caption"
              fontWeight="bold"
              color="text"
              textTransform="uppercase"
            >
              {t.empty.examplesTitle}
            </SoftTypography>
            {exampleKeys.map((key) => (
              <SoftButton
                key={key}
                variant="outlined"
                color="secondary"
                onClick={() => setBrief(examples[key])}
                data-testid={`provider-site-example-${key}`}
                sx={{ justifyContent: "flex-start", textAlign: "left", textTransform: "none" }}
              >
                {examples[key]}
              </SoftButton>
            ))}
          </SoftBox>
        </Card>
      </Grid>
      <Grid item xs={12} lg={7}>
        <Card>
          <SoftBox p={3} display="flex" flexDirection="column" gap={1.5}>
            <SoftTypography component="label" variant="caption" fontWeight="bold">
              {t.empty.inputLabel}
            </SoftTypography>
            <SoftInput
              multiline
              rows={4}
              value={brief}
              placeholder={t.empty.placeholder}
              onChange={(event: { target: { value: string } }) => setBrief(event.target.value)}
              inputProps={{
                "aria-label": t.empty.inputLabel,
                "data-testid": "provider-site-brief-input",
              }}
            />
            <SoftButton
              variant="gradient"
              color="info"
              size="large"
              disabled={!canCreate}
              onClick={() => void onCreate()}
              data-testid="provider-site-create-button"
            >
              {isSending ? <CircularProgress size={16} color="inherit" /> : t.empty.create}
            </SoftButton>
            <SoftTypography variant="caption" color="text" textAlign="center">
              {t.empty.hint}
            </SoftTypography>
          </SoftBox>
        </Card>
      </Grid>
    </Grid>
  );
}

// "Описати свій сайт": the first message to the AI designer, which builds the site from a brief.
export function SiteDescribePage() {
  return <SiteLayout>{({ profileUuid }) => <DescribeSite profileUuid={profileUuid} />}</SiteLayout>;
}
